// src/pages/PerfisCliente.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "../axiosConfig";
import {
  CheckCircle2,
  Pencil,
  Plus,
  Save,
  ShieldCheck,
  Trash2,
  UserRoundCog,
} from "lucide-react";

import "./PerfisCliente.css";

function PerfisCliente() {
  const [perfis, setPerfis] = useState([]);
  const [editandoId, setEditandoId] = useState(null);
  const [novoPerfil, setNovoPerfil] = useState({
    nome: "",
    pontos_minimos: "",
  });

  useEffect(() => {
    buscarPerfis();
  }, []);

  const buscarPerfis = () => {
    axios
      .get("/perfis-cliente")
      .then((res) => setPerfis(Array.isArray(res.data) ? res.data : []))
      .catch((err) => console.error("Erro ao buscar perfis:", err));
  };

  const handleChange = (e, id) => {
    const { name, value } = e.target;

    setPerfis((prev) =>
      prev.map((p) => (p.id === id ? { ...p, [name]: value } : p))
    );
  };

  const handleNovoChange = (e) => {
    const { name, value } = e.target;
    setNovoPerfil((prev) => ({ ...prev, [name]: value }));
  };

  const salvarNovoPerfil = () => {
    if (!novoPerfil.nome || !novoPerfil.pontos_minimos) return;

    axios
      .post("/perfis-cliente", novoPerfil)
      .then(() => {
        setNovoPerfil({ nome: "", pontos_minimos: "" });
        buscarPerfis();
      })
      .catch((err) => console.error("Erro ao adicionar perfil:", err));
  };

  const salvarEdicao = (id) => {
    const perfil = perfis.find((p) => p.id === id);

    axios
      .put(`/perfis-cliente/${id}`, {
        nome: perfil.nome,
        pontos_minimos: perfil.pontos_minimos,
      })
      .then(() => setEditandoId(null))
      .catch((err) => console.error("Erro ao editar perfil:", err));
  };

  const excluirPerfil = (id) => {
    if (!window.confirm("Deseja realmente excluir este perfil?")) return;

    axios
      .delete(`/perfis-cliente/${id}`)
      .then(() => buscarPerfis())
      .catch((err) => console.error("Erro ao excluir perfil:", err));
  };

  const resumo = useMemo(() => {
    const total = perfis.length;
    const maiorPontuacao = perfis.reduce(
      (max, perfil) => Math.max(max, Number(perfil.pontos_minimos || 0)),
      0
    );

    return {
      total,
      maiorPontuacao,
    };
  }, [perfis]);

  const formatarPontos = (valor) =>
    Number(valor || 0).toLocaleString("pt-BR", {
      maximumFractionDigits: 0,
    });

  return (
    <div className="perfisClienteAdmin-page">
      <div className="perfisClienteAdmin-topbar">
        <div>
          <span className="perfisClienteAdmin-kicker">
            Configuração de clientes
          </span>

          <h2>Perfis de clientes</h2>

          <p>
            Defina os níveis de clientes com base nos pontos mínimos acumulados
            no programa Tikpontos.
          </p>
        </div>
      </div>

      <div className="perfisClienteAdmin-cards">
        <div className="perfisClienteAdmin-card">
          <span>Total de perfis</span>
          <strong>{resumo.total.toLocaleString("pt-BR")}</strong>
        </div>

        <div className="perfisClienteAdmin-card destaque">
          <span>Maior faixa cadastrada</span>
          <strong>{formatarPontos(resumo.maiorPontuacao)} pts</strong>
        </div>
      </div>

      <div className="perfisClienteAdmin-formCard">
        <div className="perfisClienteAdmin-formHeader">
          <div className="perfisClienteAdmin-icon">
            <Plus size={20} />
          </div>

          <div>
            <h3>Novo perfil</h3>
            <span>Cadastre uma nova faixa de pontuação para clientes.</span>
          </div>
        </div>

        <div className="perfisClienteAdmin-form">
          <div className="perfisClienteAdmin-field">
            <label>Nome do perfil</label>
            <input
              type="text"
              name="nome"
              placeholder="Ex: Platinum"
              value={novoPerfil.nome}
              onChange={handleNovoChange}
            />
          </div>

          <div className="perfisClienteAdmin-field">
            <label>Pontos mínimos</label>
            <input
              type="number"
              name="pontos_minimos"
              placeholder="Ex: 10000"
              value={novoPerfil.pontos_minimos}
              onChange={handleNovoChange}
            />
          </div>

          <button
            type="button"
            className="perfisClienteAdmin-btn primary"
            onClick={salvarNovoPerfil}
          >
            <Save size={16} />
            Salvar perfil
          </button>
        </div>
      </div>

      <div className="perfisClienteAdmin-tableCard">
        <div className="perfisClienteAdmin-tableHeader">
          <div>
            <h3>Perfis cadastrados</h3>
            <span>{perfis.length.toLocaleString("pt-BR")} perfil(is)</span>
          </div>

          <UserRoundCog size={18} />
        </div>

        <div className="perfisClienteAdmin-tableWrapper">
          <table className="perfisClienteAdmin-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Nome do perfil</th>
                <th>Pontos mínimos</th>
                <th>Ações</th>
              </tr>
            </thead>

            <tbody>
              {perfis.length === 0 ? (
                <tr>
                  <td colSpan="4" className="perfisClienteAdmin-empty">
                    Nenhum perfil cadastrado.
                  </td>
                </tr>
              ) : (
                perfis.map((perfil) => {
                  const editando = editandoId === perfil.id;

                  return (
                    <tr key={perfil.id}>
                      <td>
                        <strong>#{perfil.id}</strong>
                      </td>

                      <td>
                        {editando ? (
                          <input
                            className="perfisClienteAdmin-inlineInput"
                            name="nome"
                            value={perfil.nome}
                            onChange={(e) => handleChange(e, perfil.id)}
                          />
                        ) : (
                          <div className="perfisClienteAdmin-profileCell">
                            <div className="perfisClienteAdmin-profileIcon">
                              <ShieldCheck size={15} />
                            </div>

                            <strong>{perfil.nome}</strong>
                          </div>
                        )}
                      </td>

                      <td>
                        {editando ? (
                          <input
                            className="perfisClienteAdmin-inlineInput"
                            name="pontos_minimos"
                            type="number"
                            value={perfil.pontos_minimos}
                            onChange={(e) => handleChange(e, perfil.id)}
                          />
                        ) : (
                          <span className="perfisClienteAdmin-points">
                            {formatarPontos(perfil.pontos_minimos)} pts
                          </span>
                        )}
                      </td>

                      <td>
                        <div className="perfisClienteAdmin-actions">
                          {editando ? (
                            <button
                              type="button"
                              className="perfisClienteAdmin-actionBtn save"
                              onClick={() => salvarEdicao(perfil.id)}
                            >
                              <CheckCircle2 size={14} />
                              Salvar
                            </button>
                          ) : (
                            <button
                              type="button"
                              className="perfisClienteAdmin-actionBtn edit"
                              onClick={() => setEditandoId(perfil.id)}
                            >
                              <Pencil size={14} />
                              Editar
                            </button>
                          )}

                          <button
                            type="button"
                            className="perfisClienteAdmin-actionBtn delete"
                            onClick={() => excluirPerfil(perfil.id)}
                          >
                            <Trash2 size={14} />
                            Excluir
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

export default PerfisCliente;